import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Fragment, useEffect, useState } from 'react';
import { api } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Alert } from '../layout';
import { PartnerAgreements, type Agreement } from './agreements';

type Kind = 'customer' | 'supplier' | 'both' | 'person' | 'related' | 'other';
export interface Summary {
  key: string; name: string; kind: Kind; codes: string[]; ibans: string[];
  issued: { count: number; total: number }; received: { count: number; total: number }; moneyIn: number; moneyOut: number; balance: number;
  missing: { unpaid: number; unpaidAmount: number; noReason: number; noReasonAmount: number; awaitingDocument: number }; lastDate: string | null;
}
interface CardRow {
  date: string; type: 'invoice' | 'payment' | 'cash' | 'manual'; ref: { kind: 'invoice' | 'tx' | 'cash'; id: number }; label: string;
  invoice: number; received: number; paid: number; effect: number; counts: boolean; note: string; flag: 'unpaid' | 'no_reason' | 'awaiting_document' | null; open: number; balance: number;
}
interface Card {
  partner: { key: string; name: string; kind: Kind; autoKind: Kind; keys: string[]; codes: string[]; ibans: string[]; names: string[] };
  opening: number; rows: CardRow[]; closing: number; totals: { invoice: number; received: number; paid: number }; agreements: Agreement[];
}
interface Transfer { id: number; date: string; amount: number; from: string; to: string; fromIban: string; toIban: string; own: boolean; category: string | null; note: string; purpose: string }
interface Pair { from: string; to: string; fromIban: string; toIban: string; count: number; total: number }

export const KIND_LABELS: Record<Kind, string> = { customer: 'Klientas', supplier: 'Tiekėjas', both: 'Klientas ir tiekėjas', person: 'Asmuo', related: 'Susijusi įmonė', other: 'Kita' };
const FLAG_LABELS: Record<NonNullable<CardRow['flag']>, string> = { unpaid: 'neapmokėta', no_reason: 'nėra priežasties', awaiting_document: 'laukiama dokumento' };

/** "Burak Pervaz skolingas Arkabi MB 700,00 €" — from the balance's sign. */
export function balanceText(t: ReturnType<typeof useI18n>['t'], balance: number, partner: string, us: string): string {
  if (!balance) return t('Skolų nėra');
  return balance > 0 ? t('{a} skolingas {b} {amount}', { a: partner, b: us, amount: money(balance) }) : t('{a} skolingas {b} {amount}', { a: us, b: partner, amount: money(-balance) });
}

/** Partneriai: everyone the company deals with, their documents and money, the balance and what is missing. */
export function PartnersTab({ us, onAct }: { us: string; onAct: (p: { key: string; name: string; side: 'customer' | 'supplier' }) => void }) {
  const { t } = useI18n();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [missing, setMissing] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const query = new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}), ...(kind ? { kind } : {}), ...(q ? { q } : {}), ...(missing ? { missing: '1' } : {}) }).toString();
  const list = useQuery({ queryKey: ['partners', query], queryFn: () => api.get<{ partners: Summary[] }>(`api/v1/partners?${query}`) });
  const rows = list.data?.partners ?? [];
  const sum = (f: (p: Summary) => number) => rows.reduce((s, p) => s + f(p), 0);
  return (
    <>
      <div className="recon-head">
        <label><span>{t('Nuo')}</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label><span>{t('Iki')}</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <label><span>{t('Tipas')}</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">{t('Visi')}</option>
            {(Object.keys(KIND_LABELS) as Kind[]).map((k) => <option key={k} value={k}>{t(KIND_LABELS[k])}</option>)}
          </select>
        </label>
        <label><span>{t('Paieška')}</span><input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('Pavadinimas, kodas, IBAN')} /></label>
        <label className="recurring-check"><input type="checkbox" checked={missing} onChange={(e) => setMissing(e.target.checked)} /> {t('Tik su trūkumais')}</label>
      </div>
      {list.isError ? <Alert kind="error">{errorText(t, list.error)}</Alert> : null}
      <section className="panel-card">
        <div className="table-wrap">
          <table className="partner-table partner-list">
            <thead><tr><th>{t('Partneris')}</th><th className="right">{t('Išrašyta')}</th><th className="right">{t('Gauta sąsk.')}</th><th className="right">{t('Gauta pinigų')}</th><th className="right">{t('Išmokėta')}</th><th className="right">{t('Likutis')}</th><th>{t('Trūksta')}</th></tr></thead>
            <tbody>
              {list.isSuccess && !rows.length ? <tr><td colSpan={7} className="empty">{t('Įrašų nėra.')}</td></tr> : null}
              {rows.map((p) => (<Fragment key={p.key}>
                <tr className={open === p.key ? 'is-open' : undefined} onClick={() => setOpen(open === p.key ? null : p.key)}>
                  <td><strong>{p.name}</strong><small>{t(KIND_LABELS[p.kind])}{p.codes[0] ? ` · ${p.codes[0]}` : ''}{p.ibans[0] ? ` · …${p.ibans[0].slice(-4)}` : ''}</small></td>
                  <td className="right">{p.issued.count ? <>{money(p.issued.total)}<small>{p.issued.count} {t('sąsk.')}</small></> : '—'}</td>
                  <td className="right">{p.received.count ? <>{money(p.received.total)}<small>{p.received.count} {t('sąsk.')}</small></> : '—'}</td>
                  <td className="right">{p.moneyIn ? money(p.moneyIn) : '—'}</td>
                  <td className="right">{p.moneyOut ? money(p.moneyOut) : '—'}</td>
                  <td className="right"><strong className={p.balance > 0 ? 'partner-owes' : p.balance < 0 ? 'partner-we-owe' : undefined}>{p.balance ? money(Math.abs(p.balance)) : '0,00 €'}</strong>
                    <small>{p.balance > 0 ? t('skolingas mums') : p.balance < 0 ? t('mes skolingi') : ''}</small></td>
                  <td>
                    {p.missing.unpaid ? <span className="partner-flag">{t('neapmokėtos: {n}', { n: p.missing.unpaid })}</span> : null}
                    {p.missing.noReason ? <span className="partner-flag">{t('be priežasties: {n}', { n: p.missing.noReason })}</span> : null}
                    {p.missing.awaitingDocument ? <span className="partner-flag">{t('laukia dokumento: {n}', { n: p.missing.awaitingDocument })}</span> : null}
                  </td>
                </tr>
                {/* The card opens right under its row. */}
                {open === p.key ? <tr className="partner-card-row"><td colSpan={7}><PartnerCard partnerKey={p.key} us={us} from={from} to={to} onClose={() => setOpen(null)} onAct={onAct} all={rows} /></td></tr> : null}
              </Fragment>))}
              {rows.length ? (
                <tr className="recon-sum"><td>{t('Iš viso')}</td><td className="right">{money(sum((p) => p.issued.total))}</td><td className="right">{money(sum((p) => p.received.total))}</td>
                  <td className="right">{money(sum((p) => p.moneyIn))}</td><td className="right">{money(sum((p) => p.moneyOut))}</td><td className="right">{money(sum((p) => p.balance))}</td><td /></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function NoteInput({ row, onSaved }: { row: CardRow; onSaved: () => void }) {
  const { t } = useI18n();
  const [value, setValue] = useState(row.note);
  useEffect(() => setValue(row.note), [row.note]);
  const save = useMutation({ mutationFn: () => api.patch('api/v1/partners/notes', { kind: row.ref.kind, id: row.ref.id, note: value }), onSuccess: onSaved });
  if (row.ref.kind === 'cash') return <span className="muted">{row.note}</span>;
  return <input className="partner-note" value={value} maxLength={500} placeholder={t('Pastaba…')} onChange={(e) => setValue(e.target.value)}
    onBlur={() => { if (value.trim() !== row.note) save.mutate(); }} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />;
}

/** One partner: everything in date order with the running balance, notes, kind, joining another partner. */
export function PartnerCard({ partnerKey, us, from, to, onClose, onAct, all }: { partnerKey: string; us: string; from: string; to: string; onClose: () => void; onAct: (p: { key: string; name: string; side: 'customer' | 'supplier' }) => void; all: Summary[] }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const query = new URLSearchParams({ key: partnerKey, ...(from ? { from } : {}), ...(to ? { to } : {}) }).toString();
  const card = useQuery({ queryKey: ['partner', query], queryFn: () => api.get<Card>(`api/v1/partners/card?${query}`) });
  const refresh = () => Promise.all(['partner', 'partners', 'partner-transfers', 'bank'].map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
  const settings = useMutation({ mutationFn: (body: { kind?: string; name?: string }) => api.put(`api/v1/partners/settings?${new URLSearchParams({ key: partnerKey })}`, body), onSuccess: refresh });
  const merge = useMutation({ mutationFn: (into: string) => api.post('api/v1/partners/merge', { key: partnerKey, into }), onSuccess: async () => { await refresh(); onClose(); } });
  const c = card.data;
  if (!c) return card.isError ? <Alert kind="error">{errorText(t, card.error)}</Alert> : null;
  const p = c.partner;
  const side = p.kind === 'supplier' ? 'supplier' : p.kind === 'customer' || p.kind === 'both' ? 'customer' : null;
  const actKey = p.key.replace(/^([ckn]):/, '$1');
  return (
    <section className="panel-card partner-card">
      <div className="card-head">
        <div><p className="eyebrow">{t(KIND_LABELS[p.kind])}</p><h2>{p.name}</h2>
          <small className="muted">{[...p.codes.map((x) => `${t('kodas')} ${x}`), ...p.ibans].join(' · ')}</small></div>
        <button type="button" className="text-link" onClick={onClose}>{t('Uždaryti')}</button>
      </div>
      <div className="partner-tools">
        <label><span>{t('Tipas')}</span>
          <select value={p.kind === p.autoKind ? 'auto' : p.kind} onChange={(e) => settings.mutate({ kind: e.target.value })}>
            <option value="auto">{t('Automatiškai')} ({t(KIND_LABELS[p.autoKind])})</option>
            <option value="person">{t('Asmuo')}</option>
            <option value="related">{t('Susijusi įmonė')}</option>
            <option value="customer">{t('Klientas')}</option>
            <option value="supplier">{t('Tiekėjas')}</option>
          </select>
        </label>
        <label><span>{t('Tas pats partneris kaip…')}</span>
          <select value="" onChange={(e) => { if (e.target.value && confirm(t('Sujungti „{a}“ su „{b}“?', { a: p.name, b: all.find((x) => x.key === e.target.value)?.name ?? '' }))) merge.mutate(e.target.value); }}>
            <option value="">—</option>
            {all.filter((x) => x.key !== p.key).map((x) => <option key={x.key} value={x.key}>{x.name}</option>)}
          </select>
        </label>
        <a className="button button--ghost button--small" href={`api/v1/partners/card.csv?${query}`}>CSV</a>
        {side ? <button type="button" className="button button--ghost button--small" onClick={() => onAct({ key: actKey, name: p.name, side })}>{t('Suderinimo aktas')}</button> : null}
      </div>
      {settings.isError || merge.isError ? <Alert kind="error">{errorText(t, settings.error ?? merge.error)}</Alert> : null}
      <PartnerAgreements partnerKey={p.key} kind={p.kind} agreements={c.agreements} />
      {p.kind === 'related' ? <p className="muted">{t('Šios įmonės pervedimai automatiškai pažymimi kaip pervedimai susijusiai įmonei ir rodomi skiltyje „Vidiniai pervedimai“.')}</p> : null}
      <div className="table-wrap">
        <table className="partner-table">
          <thead><tr><th>{t('Data')}</th><th>{t('Įrašas')}</th><th className="right">{t('Sąskaita')}</th><th className="right">{t('Gauta')}</th><th className="right">{t('Išmokėta')}</th><th className="right">{t('Likutis')}</th><th>{t('Pastaba')}</th></tr></thead>
          <tbody>
            <tr className="recon-sum"><td>{from || '…'}</td><td>{t('Likutis pradžioje')}</td><td /><td /><td /><td className="right">{money(c.opening)}</td><td /></tr>
            {c.rows.map((r, i) => (
              <tr key={`${r.ref.kind}-${r.ref.id}-${i}`} className={r.counts ? undefined : 'is-neutral'}>
                <td>{r.date}</td>
                <td>
                  {r.ref.kind === 'invoice' ? <Link to={r.invoice < 0 ? '/incoming/$id' : '/outgoing/$id'} params={{ id: String(r.ref.id) }}>{r.label}</Link> : r.label}
                  {r.flag ? <small className="partner-flag">{t(FLAG_LABELS[r.flag])}{r.open ? ` · ${money(r.open)}` : ''}</small> : null}
                  {!r.counts ? <small className="muted">{t('neįtraukiama į likutį')}</small> : null}
                </td>
                <td className="right">{r.invoice ? money(r.invoice) : ''}</td>
                <td className="right">{r.received ? money(r.received) : ''}</td>
                <td className="right">{r.paid ? money(r.paid) : ''}</td>
                <td className="right">{money(r.balance)}</td>
                <td><NoteInput row={r} onSaved={() => void refresh()} /></td>
              </tr>
            ))}
            <tr className="recon-sum"><td>{to || '…'}</td><td>{t('Iš viso / likutis pabaigoje')}</td><td className="right">{money(c.totals.invoice)}</td><td className="right">{money(c.totals.received)}</td>
              <td className="right">{money(c.totals.paid)}</td><td className="right">{money(c.closing)}</td><td /></tr>
          </tbody>
        </table>
      </div>
      <p className="recon-result">{balanceText(t, c.closing, p.name, us)}</p>
      <p className="muted">{t('Likutis: mūsų sąskaitos ir mūsų mokėjimai jam didina jo skolą; jo sąskaitos ir jo mokėjimai mums ją mažina. Atlyginimai, dividendai, mokesčiai, kapitalas ir banko mokesčiai rodomi, bet į likutį neįtraukiami.')}</p>
    </section>
  );
}

/** Vidiniai pervedimai: own accounts and related companies, with reasons, notes and totals per direction. */
export function TransfersTab() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const query = new URLSearchParams({ ...(from ? { from } : {}), ...(to ? { to } : {}) }).toString();
  const data = useQuery({ queryKey: ['partner-transfers', query], queryFn: () => api.get<{ transfers: Transfer[]; pairs: Pair[] }>(`api/v1/partners-transfers?${query}`) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['partner-transfers'] });
  const d = data.data;
  return (
    <>
      <div className="recon-head">
        <label><span>{t('Nuo')}</span><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label><span>{t('Iki')}</span><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
      </div>
      <p className="muted">{t('Pervedimai tarp savų sąskaitų ir su susijusiomis įmonėmis: sąskaitos nereikia, bet reikia priežasties. Įmonę susijusia pažymėkite partnerio kortelėje (Tipas → Susijusi įmonė).')}</p>
      {d?.pairs.length ? (
        <section className="panel-card">
          <h2>{t('Suvestinė pagal kryptį')}</h2>
          <div className="table-wrap">
            <table className="partner-table">
              <thead><tr><th>{t('Iš')}</th><th>{t('Į')}</th><th className="right">{t('Pervedimai')}</th><th className="right">{t('Suma')}</th></tr></thead>
              <tbody>{d.pairs.map((p) => <tr key={`${p.fromIban}-${p.toIban}`}><td>{p.from}<small>{p.fromIban}</small></td><td>{p.to}<small>{p.toIban}</small></td><td className="right">{p.count}</td><td className="right"><strong>{money(p.total)}</strong></td></tr>)}</tbody>
            </table>
          </div>
        </section>
      ) : null}
      <section className="panel-card">
        <div className="table-wrap">
          <table className="partner-table">
            <thead><tr><th>{t('Data')}</th><th>{t('Iš')}</th><th>{t('Į')}</th><th className="right">{t('Suma')}</th><th>{t('Priežastis')}</th><th>{t('Pastaba')}</th></tr></thead>
            <tbody>
              {data.isSuccess && !d?.transfers.length ? <tr><td colSpan={6} className="empty">{t('Įrašų nėra.')}</td></tr> : null}
              {d?.transfers.map((x) => (
                <tr key={x.id}>
                  <td>{x.date}</td><td>{x.from}</td><td>{x.to}</td><td className="right">{money(x.amount)}</td>
                  <td>{x.category === 'own_transfer' ? t('Pervedimas tarp savų sąskaitų') : x.category === 'related_transfer' ? t('Pervedimas susijusiai įmonei') : <span className="partner-flag">{t('nėra priežasties')}</span>}<small>{x.purpose}</small></td>
                  <td><NoteInput row={{ ref: { kind: 'tx', id: x.id }, note: x.note } as CardRow} onSaved={() => void refresh()} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
